import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConference } from '@/context/ConferenceContext';
import { getMembersByUser } from '@/services/api';
import { toast } from 'sonner';

const SpeakersPage = () => {
  const { shortName, loading: confLoading, importantDetails, siteConfig, loginDetails } = useConference();
  const [speakers, setSpeakers] = useState(() => {
    // Initialize with cached data for instant display
    try {
      const cached = localStorage.getItem('conferenceSpeakers');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);

  useEffect(() => {
    const fetchSpeakers = async () => {
      // Don't wait for confLoading or loginDetails if we have cached data
      const username = loginDetails?.username || localStorage.getItem('conferenceUsername');
      if (!username) {
        // Try to get from cached login details
        try {
          const cachedLogin = localStorage.getItem('conferenceLoginDetails');
          if (cachedLogin) {
            const parsed = JSON.parse(cachedLogin);
            if (parsed?.username) {
              localStorage.setItem('conferenceUsername', parsed.username);
              await loadSpeakers(parsed.username);
              return;
            }
          }
        } catch (e) {
          console.error('Failed to get cached username:', e);
        }
        return;
      }
      
      await loadSpeakers(username);
    };

    const loadSpeakers = async (username) => {
      setLoading(true);
      try {
        const data = await getMembersByUser(username);
        setSpeakers(data || []);
        // Cache speakers data
        localStorage.setItem('conferenceSpeakers', JSON.stringify(data || []));
        localStorage.setItem('conferenceUsername', username);
      } catch (error) {
        console.error('Failed to fetch speakers:', error);
        // Keep using cached data if fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchSpeakers();
  }, [loginDetails?.username]);

  if (loading || confLoading) {
    // Show cached data while loading fresh data
    if (speakers.length > 0) {
      // Continue rendering with cached data, don't show loading spinner
    } else {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading speakers...</p>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <Helmet>
        <title>Speakers - {importantDetails?.ConferenceTitle?.replace(/<[^>]*>/g, '') || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Meet our distinguished speakers and keynote presenters" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Speakers</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Learn from leading experts, researchers, and industry professionals
              </p>
            </motion.div>
          </div>
        </section>

        {/* Speakers Grid */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {speakers.filter(speaker => speaker.category?.toLowerCase() !== 'ocm').map((speaker) => (
                <motion.div
                  key={speaker.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card-hover bg-white rounded-2xl overflow-hidden shadow-sm hover:-translate-y-1 transition-transform cursor-pointer p-6 border-2 border-gray-100"
                  onClick={() => setSelectedSpeaker(speaker)}
                  data-testid={`speaker-card-${speaker.id}`}
                >
                  <div className="text-center">
                    {speaker.photo ? (
                      <img 
                        src={speaker.photo} 
                        alt={speaker.name} 
                        className="w-32 h-32 rounded-full object-cover mx-auto mb-4 shadow-md"
                      />
                    ) : (
                      <div className="w-32 h-32 bg-gradient-to-br from-sky-600 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-md">
                        {speaker.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{speaker.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{speaker.affiliation}</p>
                    {speaker.biography && (
                      <p className="text-base text-blue-600 font-extrabold mb-3 line-clamp-2">{speaker.biography}</p>
                    )}
                    <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-sky-100 text-sky-800">
                      {speaker.speaker_category ? speaker.speaker_category.toUpperCase() : 'Speaker'}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {speakers.filter(speaker => speaker.category?.toLowerCase() !== 'ocm').length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No speakers found.</p>
              </div>
            )}
          </div>
        </section>

        {/* Speaker Modal */}
        {selectedSpeaker && (
          <Dialog open={!!selectedSpeaker} onOpenChange={() => setSelectedSpeaker(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="speaker-modal">
              <DialogHeader>
                <DialogTitle className="text-2xl text-center">{selectedSpeaker.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-center">
                  {selectedSpeaker.photo ? (
                    <img 
                      src={selectedSpeaker.photo} 
                      alt={selectedSpeaker.name} 
                      className="w-32 h-32 rounded-full object-cover mx-auto shadow-md"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gradient-to-br from-sky-600 to-blue-600 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto shadow-md">
                      {selectedSpeaker.name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-gray-700 font-medium">{selectedSpeaker.speaker_category}</p>
                  <p className="text-sky-500 font-semibold">{selectedSpeaker.affiliation}</p>
                  <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-sky-100 text-sky-800 mt-2">
                    {selectedSpeaker.speaker_category ? selectedSpeaker.speaker_category.toUpperCase() : 'Speaker'}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Biography</h4>
                  <p className="text-gray-600 leading-relaxed">{selectedSpeaker.biography}</p>
                </div>
                {selectedSpeaker.research && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Research Interests</h4>
                    <p className="text-gray-600 leading-relaxed">{selectedSpeaker.research}</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </>
  );
};

export default SpeakersPage;
